import { api, BASE_URL } from "../config";

const BILLING_URL = BASE_URL + "billing/";

const CHECKOUT_ALLOWED_PLAN_CODES = new Set(["entrepreneur", "solo", "pro"]);
const CHECKOUT_ALLOWED_SOURCES = new Set([
    "pricing_site",
    "app_billing",
    "register_pending_plan",
    "admin_manual",
    "smoke",
    "unknown",
]);
const SMOKE_CHECKOUT_STORAGE_KEY = "verdictia.smokeCheckout";

const CHECKOUT_ERROR_MESSAGES = {
    stripe_checkout_disabled: "Le paiement est temporairement indisponible.",
    checkout_blocked_test_mode_in_production: "Le paiement test est bloqué sur cet environnement.",
    checkout_already_pending: "Un paiement est déjà en cours pour ce workspace.",
    entrepreneur_pack_already_active: "Votre pack Entrepreneur est déjà actif. Utilisez l'accès existant avant de repayer.",
    checkout_requires_reconciliation: "La facturation de ce workspace doit être vérifiée par le support.",
    requires_reconciliation: "La facturation de ce workspace doit être vérifiée par le support.",
    checkout_payment_not_paid: "Le paiement n'est pas confirmé. Aucun droit n'a été ouvert.",
    webhook_checkout_session_unmatched: "Le paiement doit être vérifié par le support avant activation.",
    webhook_test_live_mismatch: "La configuration Stripe doit être vérifiée avant activation.",
};

export const normalizeBillingPlanCode = (planCode) => {
    const normalized = String(planCode || "").trim().toLowerCase();
    if (!normalized || normalized === "trial" || !CHECKOUT_ALLOWED_PLAN_CODES.has(normalized)) {
        throw new Error("Plan de facturation indisponible.");
    }
    return normalized;
};

export const normalizeCheckoutSource = (source) => {
    const normalized = String(source || "").trim().toLowerCase();
    return CHECKOUT_ALLOWED_SOURCES.has(normalized) ? normalized : "unknown";
};

export const isSmokeCheckoutEnabledLocally = () => {
    if (typeof window === "undefined" || !window.localStorage) return false;
    try {
        const value = String(window.localStorage.getItem(SMOKE_CHECKOUT_STORAGE_KEY) || "")
            .trim()
            .toLowerCase();
        return value === "true" || value === "1" || value === "enabled";
    } catch (_error) {
        return false;
    }
};

export const getAppBillingCheckoutSource = () =>
    isSmokeCheckoutEnabledLocally() ? "smoke" : "app_billing";

export const getBillingErrorCode = (error) => {
    const payload = error?.response?.data || {};
    const raw = payload.error || payload.detail || payload.code || error?.code;
    const code = String(raw || "").trim();
    return code || null;
};

export const getBillingErrorMessage = (
    error,
    fallbackMessage = "Impossible d'ouvrir le paiement pour le moment.",
) => {
    const code = getBillingErrorCode(error);
    if (code && CHECKOUT_ERROR_MESSAGES[code]) {
        return CHECKOUT_ERROR_MESSAGES[code];
    }

    const payload = error?.response?.data || {};
    const backendMessage = payload.message || payload.detail || payload.error || error?.message;
    if (typeof backendMessage === "string" && backendMessage.trim() && backendMessage !== code) {
        return backendMessage;
    }

    return fallbackMessage;
};

export const extractBillingRedirectUrl = (payload) => {
    const candidate = payload?.data && typeof payload.data === "object"
        ? payload.data
        : payload;
    const url = candidate?.url;

    if (!url || typeof url !== "string") {
        throw new Error("URL de redirection billing absente.");
    }

    return url;
};

export const redirectToBillingUrl = (url) => {
    window.location.href = url;
    return url;
};

export async function createCheckoutSession(planCode, { source = "app_billing" } = {}) {
    const normalizedPlanCode = normalizeBillingPlanCode(planCode);
    const checkoutSource = normalizeCheckoutSource(source);
    const response = await api.post(BILLING_URL + "checkout-session", {
        plan_code: normalizedPlanCode,
        source: checkoutSource,
    });
    return redirectToBillingUrl(extractBillingRedirectUrl(response.data));
}

export async function createCustomerPortalSession({ returnPath } = {}) {
    const payload = {};
    if (returnPath) {
        payload.return_path = returnPath;
    }

    const response = await api.post(BILLING_URL + "customer-portal-session", payload);
    return redirectToBillingUrl(extractBillingRedirectUrl(response.data));
}
